import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUp, CornerDownRight } from "lucide-react";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { Logo } from "@/components/layout/Logo";
import { PrimaryButton, Eyebrow } from "@/components/fionova/Primitives";
import { getOpening, getNextTurn } from "@/services/unsaidService";
import { unsaidStore } from "@/lib/unsaidStore";
import { EllieBridge } from "@/components/journey/EllieBridge";
import { journey } from "@/lib/journey";

const GENTLE_POINTS = [
  "There aren't right or wrong answers here.",
  "I'm not trying to diagnose you, label you, or put you into a category.",
  "I just want to understand what's been happening in your world — including some things you may not normally stop to think about.",
  "I'll ask you a few questions, but this isn't a test.",
];

export default function TheUnsaid() {
  const navigate = useNavigate();
  const live = (() => { try { return JSON.parse(sessionStorage.getItem("fionova_unsaid_live") || "null"); } catch { return null; } })();
  const [phase, setPhase] = useState(live?.messages?.length ? "chat" : "gentle"); // gentle | chat | transition
  const [messages, setMessages] = useState(live?.messages || []);
  const exitTo = journey.isOn() ? "/journey" : "/welcome";
  const [draft, setDraft] = useState("");
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef(null);
  const taRef = useRef(null);

  const userTurns = messages.filter((m) => m.role === "user").length;
  // Subtle, adaptive progress toward ~9 exchanges; never a "question X of Y".
  const progress = Math.min(userTurns / 9, 0.95);

  useEffect(() => {
    try {
      if (phase === "chat") sessionStorage.setItem("fionova_unsaid_live", JSON.stringify({ messages }));
      else if (phase === "transition") sessionStorage.removeItem("fionova_unsaid_live");
    } catch {}
  }, [messages, phase]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, thinking]);

  const startChat = () => {
    setPhase("chat");
    setMessages([{ id: 1, role: "guide", text: getOpening() }]);
  };

  const advance = async (text) => {
    const content = text.trim();
    if (!content || thinking) return;
    const next = [...messages, { id: Date.now(), role: "user", text: content }];
    setMessages(next);
    setDraft("");
    if (taRef.current) taRef.current.style.height = "auto";
    setThinking(true);
    const turn = await getNextTurn(next);
    setTimeout(() => {
      setThinking(false);
      if (turn.done) {
        unsaidStore.setTranscript(next);
        setPhase("transition");
      } else {
        setMessages((m) => [...m, { id: Date.now() + 1, role: "guide", text: turn.prompt }]);
      }
    }, 850);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      advance(draft);
    }
  };

  const autoGrow = (e) => {
    setDraft(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 160) + "px";
  };

  // ---- Gentle start ----
  if (phase === "gentle") {
    return (
      <OnboardingLayout
        testId="page-the-unsaid"
        onBack={() => navigate(exitTo)}
        footer={
          <div className="flex flex-col gap-6">
            <div className="text-center">
              <p className="text-lg font-semibold italic text-foreground">Just answer honestly.</p>
              <p className="mt-1 text-base text-muted-foreground">Ready?</p>
            </div>
            <PrimaryButton data-testid="unsaid-ready" onClick={startChat}>I'm Ready</PrimaryButton>
            <p className="text-center text-xs leading-relaxed text-muted-foreground/80">
              If you ever want to stop, close the app and come back later — your progress is saved.
              If you are in crisis, please reach out to a professional or call your local emergency
              line or the US 988 Lifeline.
            </p>
          </div>
        }
      >
        <div className="pt-2">
          <EllieBridge feature="unsaid" className="mb-8" />
          <Eyebrow>The Unsaid · A Gentle Start</Eyebrow>
          <h1 className="mt-4 text-3xl font-semibold leading-tight tracking-tight text-foreground">
            Before we start, I want you to know something.
          </h1>
          <div className="mt-8 rounded-2xl bg-card p-6 ring-1 ring-border">
            <ul className="space-y-4">
              {GENTLE_POINTS.map((p, i) => (
                <li key={i} className="flex gap-3 text-base leading-relaxed text-foreground/90">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </OnboardingLayout>
    );
  }

  // ---- Transition ----
  if (phase === "transition") {
    return (
      <OnboardingLayout testId="page-unsaid-transition" contentClassName="justify-center py-10">
        <div className="flex flex-col items-center text-center">
          <Logo size={64} showTagline={false} />
          <Eyebrow className="mt-8">The Unsaid</Eyebrow>
          <h1 className="mt-4 text-4xl font-semibold tracking-tight text-foreground">
            I think I have enough.
          </h1>
          <p className="mt-5 max-w-sm text-base leading-relaxed text-muted-foreground">
            Give me a moment to put together what I noticed — not just from what you said, but from
            the patterns across our conversation.
          </p>
        </div>
        <div className="mt-12">
          <PrimaryButton data-testid="unsaid-show-reveal" onClick={() => navigate("/the-unsaid/reveal")}>
            Show Me The Unsaid
          </PrimaryButton>
        </div>
      </OnboardingLayout>
    );
  }

  // ---- Conversation ----
  return (
    <div data-testid="page-unsaid-chat" className="fio-immersive flex h-[100dvh] flex-col"><div className="fio-ambient" aria-hidden><span /><i /></div>
      <div className="mx-auto flex w-full max-w-[600px] items-center gap-4 px-5 pt-4 sm:px-8">
        <button
          type="button"
          data-testid="unsaid-close"
          onClick={() => navigate(exitTo)}
          aria-label="Close"
          className="-ml-1 flex h-9 w-9 items-center justify-center rounded-full text-foreground/60 hover:bg-black/5"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-primary/15">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${Math.max(progress * 100, 6)}%` }}
            data-testid="unsaid-progress"
          />
        </div>
      </div>

      <p className="mt-4 text-center eyebrow text-xs text-muted-foreground">The Unsaid</p>

      <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar">
        <div className="mx-auto flex w-full max-w-[600px] flex-col gap-4 px-5 py-6 sm:px-8">
          <AnimatePresence initial={false}>
            {messages.map((m) => (
              <motion.div
                key={m.id}
                data-testid="unsaid-message"
                data-role={m.role}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-3xl px-5 py-3.5 text-base leading-relaxed ${
                    m.role === "user"
                      ? "rounded-br-lg bg-primary text-primary-foreground"
                      : "rounded-bl-lg bg-card text-foreground ring-1 ring-border"
                  }`}
                >
                  {m.text}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          {thinking && (
            <div className="flex justify-start" data-testid="unsaid-thinking">
              <div className="flex items-center gap-1.5 rounded-3xl rounded-bl-lg bg-card px-5 py-4 ring-1 ring-border">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="h-2 w-2 animate-bounce rounded-full bg-primary/60"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-border bg-background/95 backdrop-blur">
        <div className="mx-auto w-full max-w-[600px] px-5 py-4 sm:px-8">
          <button
            type="button"
            data-testid="unsaid-skip"
            onClick={() => advance("I'd rather not answer that.")}
            disabled={thinking}
            className={`mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground ${
              thinking ? "pointer-events-none opacity-0" : "opacity-100"
            }`}
          >
            <CornerDownRight className="h-4 w-4" />
            I'd rather not answer that
          </button>
          <div className="flex items-end gap-2 rounded-3xl bg-card p-2 ring-1 ring-border focus-within:ring-2 focus-within:ring-primary">
            <textarea
              ref={taRef}
              data-testid="unsaid-input"
              rows={1}
              value={draft}
              onChange={autoGrow}
              onKeyDown={onKeyDown}
              placeholder="Take your time…"
              className="max-h-40 flex-1 resize-none bg-transparent px-3 py-2.5 text-base text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
            />
            <button
              type="button"
              data-testid="unsaid-send"
              onClick={() => advance(draft)}
              disabled={!draft.trim() || thinking}
              aria-label="Send"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-all enabled:hover:bg-primary/90 disabled:opacity-40"
            >
              <ArrowUp className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
